import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getSchool, updateSchool } from '../../services/schoolService';
import Loader from '../common/Loader';
import StateCitySelect from '../org/StateCitySelect';
import { Field, Section, SCHOOL_TYPES, inputCls } from '../org/orgFormUtils';

export default function EditSchool() {
  const { id } = useParams();
  const navigate = useNavigate();
  const locationRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await getSchool(id);
        const s = data.data;
        setForm({
          school_name: s.school_name || '',
          district_name: s.district_name || '',
          lea_id: s.lea_id || '',
          nces_school_id: s.nces_school_id || '',
          school_type: s.school_type || '',
          grade_low: s.grade_low || '',
          grade_high: s.grade_high || '',
          state_code: s.state_code || '',
          city: s.city || '',
          address_line1: s.address_line1 || '',
          zipcode: s.zipcode || '',
          phone: s.phone || '',
          is_active: s.is_active !== false,
        });
      } catch {
        navigate('/schools');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, navigate]);

  const set = (field) => (e) => {
    const value = field === 'is_active' ? e.target.value === 'true' : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!locationRef.current?.validate()) {
      setError('Please select a valid state and city from the dropdown.');
      return;
    }
    setSaving(true);
    const payload = { ...form };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '' || payload[k] === null) delete payload[k];
    });
    try {
      await updateSchool(id, payload);
      navigate(`/schools/${id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update school.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader text="Loading school..." />;
  if (!form) return null;

  return (
    <div className="mx-auto w-full min-w-0 max-w-[min(100%,64rem)] space-y-5">
      <div className="flex items-center gap-3">
        <Link to={`/schools/${id}`} className="p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Edit School</h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm">Update school details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <Section title="School Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="School Name *">
              <input required className={inputCls} value={form.school_name} onChange={set('school_name')} />
            </Field>
            <Field label="District / LEA Name">
              <input className={inputCls} value={form.district_name} onChange={set('district_name')} />
            </Field>
            <Field label="LEA ID">
              <input className={inputCls} value={form.lea_id} onChange={set('lea_id')} />
            </Field>
            <Field label="NCES School ID">
              <input className={inputCls} value={form.nces_school_id} onChange={set('nces_school_id')} />
            </Field>
            <Field label="School Type">
              <select className={inputCls} value={form.school_type} onChange={set('school_type')}>
                <option value="">Select type</option>
                {SCHOOL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Grade Low">
              <input className={inputCls} value={form.grade_low} onChange={set('grade_low')} />
            </Field>
            <Field label="Grade High">
              <input className={inputCls} value={form.grade_high} onChange={set('grade_high')} />
            </Field>
            <Field label="Status">
              <select className={inputCls} value={String(form.is_active)} onChange={set('is_active')}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </Field>
          </div>
        </Section>

        <Section title="Contact & Address">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StateCitySelect
              ref={locationRef}
              stateCode={form.state_code}
              city={form.city}
              stateRequired
              cityRequired
              onStateChange={(value) => setForm((prev) => ({ ...prev, state_code: value, city: '' }))}
              onCityChange={(value) => setForm((prev) => ({ ...prev, city: value }))}
            />
            <Field label="Zipcode">
              <input className={inputCls} value={form.zipcode} onChange={set('zipcode')} />
            </Field>
            <Field label="Phone">
              <input className={inputCls} value={form.phone} onChange={set('phone')} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Address">
                <input className={inputCls} value={form.address_line1} onChange={set('address_line1')} />
              </Field>
            </div>
          </div>
        </Section>

        <div className="flex justify-end gap-3">
          <Link to={`/schools/${id}`} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-700 dark:text-slate-200">
            Cancel
          </Link>
          <button type="submit" disabled={saving} className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
